import type { Vehicle } from "@/types";

// Paleta fixa de cores de veículo (modo "cor fixa"). Chave estável usada na
// persistência (attributes do Traccar) — o hex pode ser ajustado aqui sem
// quebrar nada, pois o que é salvo é a chave, não o valor.
export type VehicleColorKey =
  | "white"
  | "black"
  | "silver"
  | "blue"
  | "red"
  | "green"
  | "yellow"
  | "orange"
  | "purple";

export const VEHICLE_COLOR_PALETTE: { key: VehicleColorKey; label: string; hex: string }[] = [
  { key: "white", label: "Branco", hex: "#f8fafc" },
  { key: "black", label: "Preto", hex: "#111827" },
  { key: "silver", label: "Prata", hex: "#94a3b8" },
  { key: "blue", label: "Azul", hex: "#2563eb" },
  { key: "red", label: "Vermelho", hex: "#dc2626" },
  { key: "green", label: "Verde", hex: "#16a34a" },
  { key: "yellow", label: "Amarelo", hex: "#eab308" },
  { key: "orange", label: "Laranja", hex: "#f97316" },
  { key: "purple", label: "Roxo", hex: "#9333ea" },
];

export const VEHICLE_COLOR_MAP: Record<VehicleColorKey, string> = Object.fromEntries(
  VEHICLE_COLOR_PALETTE.map((c) => [c.key, c.hex]),
) as Record<VehicleColorKey, string>;

export const DEFAULT_VEHICLE_COLOR: VehicleColorKey = "white";

// Cinza neutro usado só na pré-visualização do modelo quando o modo de cor é
// "Automática" (a cor real no mapa depende do status em tempo real). Fonte
// única — usado pelo picker e pelo gerador de ícones fotorrealistas
// (scripts/render-vehicle-icons), que precisa cobrir 100% das cores usadas
// no app já que modelos fotorrealistas não têm fallback SVG.
export const AUTO_PREVIEW_COLOR = "#64748b";

// ---------------------------------------------------------------------------
// Cor automática por status — ÚNICA fonte de verdade para essa lógica. Se o
// mapeamento de cores por status precisar mudar no futuro, muda só aqui.
// ---------------------------------------------------------------------------
export const STATUS_TINTS = {
  ligado: "#22c55e", // verde
  parado: "#3b82f6", // azul
  offline: "#ef4444", // vermelho (regra alterada 2026-08-31 a pedido do usuário)
  semGps: "#f97316", // laranja
  bloqueado: "#ef4444", // vermelho
} as const;

export function resolveAutoColor(v: Vehicle): string {
  if (v.blocked) return STATUS_TINTS.bloqueado;
  if (v.status === "offline") return STATUS_TINTS.offline;
  if (v.hasPosition === false || v.gpsSignal === false) return STATUS_TINTS.semGps;
  if (v.moving) return STATUS_TINTS.ligado;
  return STATUS_TINTS.parado;
}

// Cor final do ícone no mapa: fixa (se configurada) ou automática por status.
export function resolveVehicleColor(v: Vehicle): string {
  if (v.iconColorMode === "fixed" && v.iconColor && v.iconColor in VEHICLE_COLOR_MAP) {
    return VEHICLE_COLOR_MAP[v.iconColor as VehicleColorKey];
  }
  return resolveAutoColor(v);
}

// Frota de carga de teste (TESTE CARGA NNNNN, ~5000 devices, ver memória
// gpsdrive_nginx_gzip_proxied_fix_2026-09-02 — mantidos de propósito no
// banco de prod) — pedido 2026-09-03: colorir cada um com uma cor variada
// (em vez do "todo mundo verde/azul" da cor automática por status) só pra
// visualizar como o mapa fica com muitas cores distintas ao mesmo tempo.
const TEST_FLEET_NAME_RE = /^TESTE CARGA\b/i;

export function isTestFleetVehicle(v: Vehicle): boolean {
  return TEST_FLEET_NAME_RE.test(v.name);
}

function hashToIndex(input: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (Math.imul(h, 31) + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % mod;
}

// Só pra frota de teste: cor determinística (hash do id) escolhida dentro de
// VEHICLE_COLOR_PALETTE — nunca um hex arbitrário, porque os ícones
// fotorrealistas só têm PNG pré-gerado pras cores da paleta (ver
// photo-registry.ts); um hex fora dela cairia no pixel transparente e o
// veículo sumiria do mapa. Fora da frota de teste, comportamento intocado.
export function resolveMapVehicleColor(v: Vehicle): string {
  if (isTestFleetVehicle(v)) {
    return VEHICLE_COLOR_PALETTE[hashToIndex(v.id, VEHICLE_COLOR_PALETTE.length)].hex;
  }
  return resolveVehicleColor(v);
}
